import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { ApiError } from '@/lib/apiClient';

interface Options<TData, TVariables> {
  mutationFn: (vars: TVariables) => Promise<TData>;
  /** Query keys to invalidate on success. */
  invalidate?: QueryKey[];
  successMessage?: string | ((data: TData, vars: TVariables) => string);
  errorMessage?: string;
  onSuccess?: (data: TData, vars: TVariables) => void | Promise<void>;
}

/**
 * Thin wrapper over `useMutation` that shows a success/error snackbar and
 * invalidates the given query keys. Keeps feature hooks tiny and consistent.
 */
export function useMutationWithToast<TData, TVariables>({
  mutationFn,
  invalidate = [],
  successMessage,
  errorMessage,
  onSuccess,
}: Options<TData, TVariables>) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation<TData, unknown, TVariables>({
    mutationFn,
    onSuccess: async (data, vars) => {
      await Promise.all(invalidate.map((key) => queryClient.invalidateQueries({ queryKey: key })));
      if (successMessage) {
        const msg =
          typeof successMessage === 'function' ? successMessage(data, vars) : successMessage;
        // An empty message (e.g. a preview run) shows no toast.
        if (msg) enqueueSnackbar(msg, { variant: 'success' });
      }
      await onSuccess?.(data, vars);
    },
    onError: (error) => {
      const message =
        errorMessage ??
        (error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
